import { useContext, useMemo } from "react";
import { ContextEDH } from "@context/ContextEDH";
import { AppContext } from "../context/EditSessionsContext";
import { EditedSession } from "../context/formData.reducer";
import { WeekLockContext } from "../utils/isWeekLocked";

/**
 * Single source for the `WeekLockContext`. The week selector (`Repeat`) and the
 * manual fork card (`EditSession` -> `WeeksForkCard`) must agree on which weeks
 * are locked; deriving it twice let them drift apart.
 *
 * `isIntervalsSectionReadOnly` stays a parameter because `Repeat` receives it as
 * the `disabled` prop while `EditSession` reads it from the abilities.
 */
export const useWeekLockContext = (
  originalWeeksBySessionId: { [sessionId: string]: string[] },
  isIntervalsSectionReadOnly: boolean,
): WeekLockContext => {
  const { state } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);

  const selectedSessions = state?.form?.selectedSessions;
  const savedSessions = state?.form?.savedSessions;
  const sessionsToCreate = state?.form?.sessionsToCreate;
  const sessionsToDelete = state?.form?.sessionsToDelete;
  const thresholdDate = state?.form?.blockingThreshold;
  const blocks = state?.form?.editedSessions?.blocks;
  const canEditPastSessions = !!stateEDH?.base?.base?.user?.abilities?.can_edit_past_sessions;
  const canUnassignIntervals = !!stateEDH?.base?.base?.user?.abilities?.can_unassign_intervals;

  const resolvedSelectedSessions = useMemo(() => {
    const editedSessionsById = [...(savedSessions ?? []), ...(sessionsToCreate ?? [])].reduce<{
      [sessionId: string]: EditedSession;
    }>((acc, session) => {
      if (session?.id) acc[session.id] = session;
      return acc;
    }, {});

    return (selectedSessions ?? []).map(
      session => editedSessionsById[session?.id] ?? (session as EditedSession),
    );
  }, [selectedSessions, savedSessions, sessionsToCreate]);

  const hasDeletedSessionSelected = useMemo(() => {
    return resolvedSelectedSessions.some(session => !!sessionsToDelete?.[session?.id ?? "-"]);
  }, [resolvedSelectedSessions, sessionsToDelete]);

  const hasClonedOrNewSessionSelected = useMemo(() => {
    return resolvedSelectedSessions.some(
      (session: EditedSession) => session?.isCloned || session?.isNew || session?.id?.includes("-"),
    );
  }, [resolvedSelectedSessions]);

  const originalSelectedWeekIds = useMemo(() => {
    const result: { [weekId: string]: string } = {};
    const selectedSessionsIds = resolvedSelectedSessions.map(session => session?.id);

    for (const sessionId in originalWeeksBySessionId) {
      if (selectedSessionsIds.includes(sessionId)) {
        originalWeeksBySessionId[sessionId]?.forEach(weekId => {
          result[weekId] = weekId;
        });
      }
    }

    return result;
  }, [originalWeeksBySessionId, resolvedSelectedSessions]);

  return useMemo(
    () => ({
      thresholdDate,
      blocks,
      selectedSessions: resolvedSelectedSessions,
      originalSelectedWeekIds,
      isIntervalsSectionReadOnly,
      hasDeletedSessionSelected,
      hasClonedOrNewSessionSelected,
      canUnassignIntervals,
      canEditPastSessions,
    }),
    [
      thresholdDate,
      blocks,
      resolvedSelectedSessions,
      originalSelectedWeekIds,
      isIntervalsSectionReadOnly,
      hasDeletedSessionSelected,
      hasClonedOrNewSessionSelected,
      canUnassignIntervals,
      canEditPastSessions,
    ],
  );
};
